import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/use-theme";

export const ThemeSection: React.FC = () => {
  const { t } = useTranslation();

  // Draft mode hook usage
  const { mode, draftTheme, setDraftTheme, isUpdatingTheme } = useTheme();

  // Use draft value if available, otherwise current mode
  const displayMode = draftTheme || mode;

  return (
    <div className="flex flex-col lg:flex-row gap-8 min-w-0">
      {/* Label - Figma: max-w-280px */}
      <div className="w-full lg:w-[280px] shrink-0">
        <h3 className="text-sm font-semibold text-text-secondary dark:text-text-secondary leading-5 mb-1">
          {t("settings.appearance.displayPreference")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-tertiary leading-5">
          {t("settings.appearance.displayPreferenceDescription")}
        </p>
      </div>

      {/* Theme Cards — 3 kolon, mevcut alana sığacak şekilde küçülür */}
      <div className="flex-1 min-w-0 grid grid-cols-3 gap-3 sm:gap-4">
        {/* System Preference Card */}
        <button
          onClick={() => setDraftTheme("system")}
          disabled={isUpdatingTheme}
          className="group flex min-w-0 flex-col gap-2 items-start disabled:opacity-50"
        >
          <div
            className={cn(
              "w-full aspect-[200/132] rounded-lg overflow-hidden transition-all",
              displayMode === "system"
                ? "ring-4 ring-black dark:ring-white shadow-[0px_0px_0px_2px_#ffffff,0px_0px_0px_4px_#000000]"
                : "ring-1 ring-border-primary dark:ring-border-primary hover:ring-2"
            )}
          >
            {/* Split View: Light/Dark */}
            <div className="w-full h-full flex">
              {/* Light Half */}
              <div className="w-1/2 h-full bg-gray-100 dark:bg-gray-800 relative">
                <ThemePreviewMockup mode="light" />
              </div>
              {/* Dark Half */}
              <div className="w-1/2 h-full bg-[#22262f] relative">
                <ThemePreviewMockup mode="dark" />
              </div>
            </div>
          </div>
          <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
            {t("settings.appearance.systemPreference")}
          </p>
        </button>

        {/* Light Mode Card */}
        <button
          onClick={() => setDraftTheme("light")}
          disabled={isUpdatingTheme}
          className="group flex min-w-0 flex-col gap-2 items-start disabled:opacity-50"
        >
          <div
            className={cn(
              "w-full aspect-[200/132] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 transition-all",
              displayMode === "light"
                ? "ring-4 ring-black dark:ring-white"
                : "ring-1 ring-border-primary dark:ring-border-primary hover:ring-2"
            )}
          >
            <ThemePreviewMockup mode="light" />
          </div>

          <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
            {t("settings.appearance.lightMode")}
          </p>
        </button>

        {/* Dark Mode Card */}
        <button
          onClick={() => setDraftTheme("dark")}
          disabled={isUpdatingTheme}
          className="group flex min-w-0 flex-col gap-2 items-start disabled:opacity-50"
        >
          <div
            className={cn(
              "w-full aspect-[200/132] rounded-lg overflow-hidden bg-[#22262f] transition-all",
              displayMode === "dark"
                ? "ring-4 ring-black dark:ring-white"
                : "ring-1 ring-border-primary dark:ring-border-primary hover:ring-2"
            )}
          >
            <ThemePreviewMockup mode="dark" />
          </div>
          <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
            {t("settings.appearance.darkMode")}
          </p>
        </button>
      </div>
    </div>
  );
};

// Theme Preview Mockup Component - Figma tasarımına göre
const ThemePreviewMockup = ({ mode }: { mode: "light" | "dark" }) => {
  const bgColor = mode === "light" ? "bg-white" : "bg-[#0c0e12]";
  const navBg = mode === "light" ? "bg-gray-50" : "bg-[#13161b]";
  const sectionBg = mode === "light" ? "bg-gray-50" : "bg-[#13161b]";
  const borderColor = mode === "light" ? "border-gray-200" : "border-[#373a41]";
  const accentBg = "bg-[#6172f3]";
  const inactiveBg = mode === "light" ? "bg-gray-100" : "bg-[#22262f]";

  return (
    <div
      className={cn(
        "w-full h-full p-[8%] relative flex flex-col gap-[6%]",
        bgColor,
        "border",
        borderColor
      )}
    >
      {/* Nav Bar with window controls */}
      <div
        className={cn(
          "w-full h-[12%] min-h-2 shrink-0 rounded-sm",
          navBg,
          borderColor,
          "border-b flex items-center px-1"
        )}
      >
        <div className="flex gap-0.5">
          <div className="w-1 h-1 rounded-full bg-gray-400" />
          <div className="w-1 h-1 rounded-full bg-gray-400" />
          <div className="w-1 h-1 rounded-full bg-gray-400" />
        </div>
      </div>

      {/* Body: sidebar + content — yüzde ile ölçeklenir */}
      <div className="flex min-h-0 flex-1 gap-[6%]">
        <div
          className={cn(
            "w-[18%] h-full flex flex-col gap-[10%]",
            borderColor,
            "border-r pr-[8%]"
          )}
        >
          <div className={cn("w-full h-[8%] rounded-sm", accentBg)} />
          <div className={cn("w-full h-[8%] rounded-sm", inactiveBg)} />
          <div className={cn("w-full h-[8%] rounded-sm", inactiveBg)} />
          <div className={cn("w-full h-[8%] rounded-sm", inactiveBg)} />
          <div className={cn("w-full h-[8%] rounded-sm", inactiveBg)} />
          <div className={cn("w-full h-[8%] rounded-sm", inactiveBg)} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-[12%]">
          <div className={cn("w-full flex-1 rounded", sectionBg)} />
          <div className={cn("w-full flex-1 rounded", sectionBg)} />
        </div>
      </div>
    </div>
  );
};
